
import Observable from '@/utils/Observable'
import config from '../config/constants'
import IO from '@pm2/js-api'

const io = new IO({
  OAUTH_CLIENT_ID: window.OAUTH_CLIENT_ID || config.OAUTH_CLIENT_ID,
  services: {
    API: config.API_URL,
    OAUTH: config.OAUTH_URL,
  },
}).use('browser')

export function getAccessToken() {
  return io._network.tokens.access_token
}

const requestCallbacks = []

export function registerRequestCallback(fn) {
  requestCallbacks.push(fn)
}

const responseCallbacks = []

export function registerResponseCallback(fn) {
  responseCallbacks.push(fn)
}

function getAsString(d) {
  return typeof d === 'string' ? d : false
}

class ErrorResponse extends Error {
  constructor(response) {
    const data = response.data || {}
    super(
      getAsString(data.msg) ||
        getAsString(data.message) ||
        getAsString(data.error_description) ||
        getAsString(data) ||
        '',
    )
    Object.assign(this, response, { data })
  }

  get isInternalError() {
    return this.status >= 500 && (!this.data || this.data.cause !== 'Stripe Error')
  }

  get shouldReportError() {
    // Only report internal errors
    if (!this.isInternalError) return false

    // Ignore 'No Living connections' because it is the most common errors and fills our event quota
    if (this.message === 'No Living connections') return false

    return true
  }
}

function reportErrorInternally(error) {
  if (!(error instanceof ErrorResponse)) {
    console.error('Unexpected pm2-js-api error:', error)
  } else if (error.isInternalError && error.shouldReportError) {
    console.error(error)
  }
}

function defaultErrorNotificationGetter(error) {
  if (window.HIDE_ERRORS) {
    return
  }

  if (error.isInternalError) {
    return 'An internal error occured. Please retry later or contact the support.'
  }

  if (error.message !== 'Unauthorized') {
    return error.message
  }
}

function wrapFunction(fn) {
  function withErrorNotification(errorNotificationGetter) {
    return (...args) => {
      // Call request callbacks
      requestCallbacks.forEach(cb => cb())
      const promise = fn(...args).catch(errorOrResponse => {
        let error
        if (!errorOrResponse) error = new Error('Empty response')
        else if (errorOrResponse instanceof Error) error = errorOrResponse
        else error = new ErrorResponse(errorOrResponse)
        return Promise.reject(error)
      })

      promise.then(
        () => {
          // Call response callbacks without error message
          responseCallbacks.forEach(cb => cb())
        },
        error => {
          // Always report error internally
          reportErrorInternally(error)
          // The message will be either 'falsy' or a string to be displayed to the user
          const message = errorNotificationGetter(error)
          // Call response callbacks with the message
          responseCallbacks.forEach(cb => cb(message))
        },
      )

      return promise
    }
  }

  const wrappedFunction = withErrorNotification(defaultErrorNotificationGetter)
  wrappedFunction.withoutErrorNotification = withErrorNotification(() => {})
  return wrappedFunction
}

function isNamespace(value) {
  return value && typeof value === 'object' && value.name && Array.isArray(value.endpoints)
}

function wrapFunctions(namespace, jsApi) {
  namespace.endpoints.forEach(endpoint => {
    jsApi[endpoint.name] = wrapFunction(namespace[endpoint.name])
  })

  Object.values(namespace).forEach(value => {
    if (isNamespace(value)) {
      jsApi[value.name] = {}
      wrapFunctions(value, jsApi[value.name])
    }
  })
}

const wrappedJsApi = {}

wrapFunctions(io, wrappedJsApi)

Object.defineProperties(wrappedJsApi, {
  realtime: { get: () => io.realtime },
  apiDateLag: { get: () => io.apiDateLag },
})
// "logout" is a special method, not contained in a namespace
Object.defineProperties(wrappedJsApi.auth, {
  logout: { value: wrapFunction(io.auth.logout) },
})

export default wrappedJsApi

export function resetEndpoints() {
  io._network._endpoints.clear()
}

// Keep track of realtime listeners
export function listenRealtimeEvents(bucket, events) {
  for (const event in events) io.realtime.on(`${bucket.public_id}:${event}`, events[event])
  return () => {
    for (const event in events) io.realtime.off(`${bucket.public_id}:${event}`, events[event])
  }
}

export function createRealtimeObservable(bucket, eventName) {
  return new Observable(observer => {
    return listenRealtimeEvents(bucket, {
      [eventName](event) {
        observer.next(event)
      },
    })
  })
}
